// components/kyc/KycRequiredModal.tsx
//
// Modal shown when KYC verification is required for an action
// If user hasn't accepted other terms, shows OtherTermsAcceptanceDialog first

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";
import KycVerificationDialog from "./KycVerificationDialog";
import OtherTermsAcceptanceDialog from "./OtherTermsAcceptanceDialog";
import { useState, useMemo } from "react";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";

interface KycRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionName?: string; // e.g., "buda", "köpa", "skicka fordonstest"
}

const KycRequiredModal = ({
  isOpen,
  onClose,
  actionName,
}: KycRequiredModalProps) => {
  const { user } = useAuth();
  const [isKycDialogOpen, setIsKycDialogOpen] = useState(false);
  const [showOtherTermsDialog, setShowOtherTermsDialog] = useState(false);
  const labels = useMemo(() => authLabels(getLanguage()), []);

  // Use i18n default if actionName not provided
  const defaultAction = labels.kyc?.required?.defaultAction || "denna åtgärd";
  const displayAction = actionName || defaultAction;

  const handleStartVerification = () => {
    // Admins should not need to accept terms or verify KYC
    if (user?.type === UserType.Admin) {
      onClose();
      return;
    }

    // First check if user has accepted other terms
    // If not, show OtherTermsAcceptanceDialog first
    if (user && user.otherTermsAccepted === false) {
      setShowOtherTermsDialog(true);
    } else {
      // User has accepted other terms, proceed directly to ID verification
      setIsKycDialogOpen(true);
    }
  };

  const handleOtherTermsAccepted = () => {
    // User has accepted other terms, now show ID verification dialog
    setShowOtherTermsDialog(false);
    setIsKycDialogOpen(true);
  };

  const handleKycDialogClose = () => {
    setIsKycDialogOpen(false);
    // Close this modal when KYC dialog closes (user can reopen if needed)
    onClose();
  };

  const handleOtherTermsDialogClose = () => {
    setShowOtherTermsDialog(false);
    // Don't close main modal - user can try again
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#D4AF37]" />
              {labels.kyc?.required?.title}
            </DialogTitle>
            <DialogDescription>
              {labels.kyc?.required?.description?.replace(
                "{action}",
                displayAction
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {labels.kyc?.required?.message}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              {labels.kyc?.required?.cancel}
            </Button>
            <Button
              onClick={handleStartVerification}
              className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
            >
              <Shield className="h-4 w-4 mr-2" />
              {labels.kyc?.required?.verifyIdentity}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Other Terms Acceptance Dialog - shown BEFORE ID verification if terms not accepted */}
      <OtherTermsAcceptanceDialog
        isOpen={showOtherTermsDialog}
        onClose={handleOtherTermsDialogClose}
        onAccepted={handleOtherTermsAccepted}
      />

      {/* ID Verification Dialog - shown AFTER other terms are accepted */}
      <KycVerificationDialog
        isOpen={isKycDialogOpen}
        onClose={handleKycDialogClose}
      />
    </>
  );
};

export default KycRequiredModal;
